import React, { useState, useEffect } from "react";
import { message, Space, Tag, Modal, Tabs, Tooltip, Table } from "antd";
import Layout from "../../components/Layout";
import { PlusOutlined, EditOutlined, DeleteOutlined, PauseCircleOutlined, PlayCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GlassCard from "../../components/premium/GlassCard";
import PremiumButton from "../../components/premium/PremiumButton";
import { getAllCampaigns, getActiveCampaigns, deleteCampaign, pauseCampaign, resumeCampaign } from "../../services/campaign/campaignServices";
import slugs from "../../resources/slugs";
import moment from "moment";

const { TabPane } = Tabs;

const CampaignList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const fetchCampaigns = async (tab) => {
    setLoading(true);
    try {
      const res = tab === "active" ? await getActiveCampaigns() : await getAllCampaigns();
      if (res?.success) {
        setCampaigns(res.data || []);
      } else {
        message.error(res?.message || "Failed to fetch campaigns");
      }
    } catch (error) {
      message.error("An error occurred while fetching campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns(activeTab);
  }, [activeTab]);

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Campaign",
      content: "Are you sure you want to delete this campaign? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      onOk: async () => {
        const res = await deleteCampaign(id);
        if (res?.success) {
          message.success("Campaign deleted successfully");
          fetchCampaigns(activeTab);
        } else {
          message.error(res?.message || "Failed to delete campaign");
        }
      },
    });
  };

  const handlePause = async (id) => {
    const res = await pauseCampaign(id);
    if (res?.success) {
      message.success("Campaign paused");
      fetchCampaigns(activeTab);
    } else {
      message.error(res?.message || "Failed to pause campaign");
    }
  };

  const handleResume = async (id) => {
    const res = await resumeCampaign(id);
    if (res?.success) {
      message.success("Campaign resumed");
      fetchCampaigns(activeTab);
    } else {
      message.error(res?.message || "Failed to resume campaign");
    }
  };

  const handleViewDetails = (record) => {
    setSelectedCampaign(record);
    setViewModalVisible(true);
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <div>
          <span style={{ fontWeight: 600 }}>{text}</span>
          <br />
          <small style={{ color: "var(--text-muted)" }}>{record.body?.substring(0, 50)}...</small>
        </div>
      ),
    },
    {
      title: "Dates",
      key: "dates",
      render: (_, record) => (
        <div style={{ fontSize: "12px" }}>
          <div>S: {moment(record.startDate).format("YYYY-MM-DD HH:mm")}</div>
          <div>E: {moment(record.endDate).format("YYYY-MM-DD HH:mm")}</div>
        </div>
      ),
    },
    {
      title: "Audience",
      dataIndex: "targetAudience",
      key: "targetAudience",
      render: (audience) => <Tag color="blue">{audience}</Tag>,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (priority) => {
        let color = "green";
        if (priority === "HIGH") color = "red";
        if (priority === "MEDIUM") color = "orange";
        return <Tag color={color}>{priority}</Tag>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let color = "default";
        if (status === "ACTIVE") color = "success";
        if (status === "PAUSED") color = "warning";
        if (status === "EXPIRED") color = "error";
        return <Tag color={color}>{status || "UNKNOWN"}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="View Details">
            <EyeOutlined 
              style={{ color: "#52c41a", cursor: "pointer" }} 
              onClick={() => handleViewDetails(record)} 
            />
          </Tooltip>
          <Tooltip title="Edit">
            <EditOutlined 
              style={{ color: "#1890ff", cursor: "pointer" }} 
              onClick={() => navigate(slugs.edit_campaign.replace(":id", record.campaignId || record.id))} 
            />
          </Tooltip>
          
          {record.status === "ACTIVE" ? (
            <Tooltip title="Pause">
              <PauseCircleOutlined 
                style={{ color: "#faad14", cursor: "pointer" }} 
                onClick={() => handlePause(record.campaignId || record.id)} 
              />
            </Tooltip>
          ) : record.status === "PAUSED" ? (
            <Tooltip title="Resume">
              <PlayCircleOutlined 
                style={{ color: "#52c41a", cursor: "pointer" }} 
                onClick={() => handleResume(record.campaignId || record.id)} 
              />
            </Tooltip>
          ) : null}

          <Tooltip title="Delete">
            <DeleteOutlined 
              style={{ color: "#ff4d4f", cursor: "pointer" }} 
              onClick={() => handleDelete(record.campaignId || record.id)} 
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <div className="campaign-list-container">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
          <div>
            <h2 style={{ margin: 0 }}>Campaigns & Promotions</h2>
            <p style={{ color: "var(--text-muted)" }}>Manage marketing campaigns and push notifications</p>
          </div>
          <PremiumButton 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={() => navigate(slugs.create_campaign)}
          >
            Create New Campaign
          </PremiumButton>
        </div>

        <GlassCard>
          <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: "16px" }}>
            <TabPane tab="All Campaigns" key="all" />
            <TabPane tab="Active Campaigns" key="active" />
          </Tabs>

          <Table 
            columns={columns} 
            dataSource={campaigns} 
            loading={loading}
            rowKey={(record) => record.campaignId || record.id}
            pagination={{ pageSize: 10 }}
          />
        </GlassCard>

        <Modal
          title="Campaign Details"
          visible={viewModalVisible}
          onCancel={() => setViewModalVisible(false)}
          footer={[
            <PremiumButton key="close" onClick={() => setViewModalVisible(false)}>
              Close
            </PremiumButton>
          ]}
          width={700}
        >
          {selectedCampaign && (
            <div style={{ padding: "10px" }}>
              <div style={{ marginBottom: "20px", textAlign: "center" }}>
                {selectedCampaign.imageUrl ? (
                  <img 
                    src={selectedCampaign.imageUrl} 
                    alt="Campaign" 
                    style={{ maxWidth: "100%", maxHeight: "300px", borderRadius: "8px", objectFit: "contain", border: "1px solid #f0f0f0" }} 
                  />
                ) : (
                  <div style={{ padding: "40px", background: "#f5f5f5", borderRadius: "8px", color: "#999" }}>
                    No Image Provided
                  </div>
                )}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}>
                <div>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>Title</h4>
                  <p style={{ fontWeight: 600, fontSize: "16px" }}>{selectedCampaign.title}</p>
                </div>
                <div>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>Status</h4>
                  <Tag color={selectedCampaign.status === "ACTIVE" ? "success" : "default"}>{selectedCampaign.status}</Tag>
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>Description</h4>
                  <p>{selectedCampaign.body}</p>
                </div>
                <div>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>Start Date</h4>
                  <p>{moment(selectedCampaign.startDate).format("LLLL")}</p>
                </div>
                <div>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>End Date</h4>
                  <p>{moment(selectedCampaign.endDate).format("LLLL")}</p>
                </div>
                <div>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>Target Audience</h4>
                  <Tag color="blue">{selectedCampaign.targetAudience}</Tag>
                </div>
                <div>
                  <h4 style={{ color: "var(--text-muted)", fontSize: "12px", textTransform: "uppercase", marginBottom: "4px" }}>Priority</h4>
                  <Tag color={selectedCampaign.priority === "HIGH" ? "red" : selectedCampaign.priority === "MEDIUM" ? "orange" : "green"}>
                    {selectedCampaign.priority}
                  </Tag>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </Layout>
  );
};

export default CampaignList;
